import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import { NavLink, NavLinkProps } from 'react-router-dom';
import useMessaging from '../../../../hooks/useMessaging';
import { SessionType } from 'models/Message';
import ResolveImage from 'components/ResolveImage';
import utils, { getUserFullName } from 'utils';
import ThreeDotsDropdown from 'components/ThreeDotsDropdown';
import { useTranslation } from 'react-i18next';
import { getImgUrl } from 'components/ResolveImage/utils';
import MessagingContext from '../../../../providers/Messaging/Messaging.context';

import './SessionCard.styles.scss';

type SessionCardProps = {
  className?: string;
  session: SessionType;
  markSessionAsUnread: (sessionId: string) => void;
} & Omit<NavLinkProps, 'to' | 'children'>;

const SessionCard: React.FC<SessionCardProps> = (props) => {
  const { session, className, markSessionAsUnread, ...rest } = props;
  const { t } = useTranslation();
  const { listingId, dealRoomId, listingEnquiryId, sessionId } =
    useContext(MessagingContext);

  const {
    interlocutor,
    latestMessageAt,
    hasUnreadMessages,
    listing,
    isExternalCommunication,
    isBuyer,
  } = session;

  const isActive = useMemo(
    () => session.id === sessionId,
    [session, sessionId],
  );

  const dealName = listing.dealRoom.portfolioName;

  const listingName = dealRoomId ? listing.listingTitle || dealName : dealName;
  const representerName = isBuyer
    ? listing.dealRoom.companyName || ''
    : getUserFullName(interlocutor?.firstName, interlocutor?.lastName);

  const shouldSwitchTitleAndName =
    !isExternalCommunication && !!dealRoomId && !!representerName;

  const { getNavigationLink } = useMessaging({
    listingId,
    dealRoomId,
    listingEnquiryId,
  });

  return (
    <div
      className={classNames(
        'session-card-wrapper',
        { 'session-card-wrapper--active': isActive },
        { 'session-card-wrapper--new-message': hasUnreadMessages },
        className,
      )}
    >
      <NavLink
        {...rest}
        to={getNavigationLink(session.id)}
        className={({ isActive }) =>
          classNames('session-card', {
            'session-card--active': isActive,
          })
        }
      >
        <ResolveImage
          className="session-card__image"
          src={getImgUrl(listing?.gallery?.[0]?.url, '100')}
        />
        <div className="session-card__content">
          <div className="session-card__content__title">
            <p>{shouldSwitchTitleAndName ? representerName : listingName}</p>
            <p
              className={classNames({
                external: isExternalCommunication,
              })}
            >
              {isExternalCommunication && <span>{t('3rdParty')}</span>}
              <span>{utils.shortPastTime(new Date(latestMessageAt))}</span>
            </p>
          </div>
          <p className="session-card__content__representer">
            {shouldSwitchTitleAndName ? listingName : representerName}
          </p>
        </div>
      </NavLink>
      {!isExternalCommunication && !hasUnreadMessages && (
        <div className="session-card-wrapper__actions">
          <ThreeDotsDropdown
            options={[
              {
                label: t('markUnread'),
                onClick: () => markSessionAsUnread(session.id),
              },
            ]}
          />
        </div>
      )}
    </div>
  );
};

export default SessionCard;
